import { resolveAccess } from './resolveAccess'
import { useAccessSources } from './useAccessSources'
import type { AuthorizationRule, NonRecordAccessRule, RecordAccessRule } from './accessRules'
import type { AskedRecord } from './askedRecord'
import type { RecordPermissionsEntity } from '@omnea-digital/authz-model'

const askedFor = (
  authorization: AuthorizationRule,
  recordId: string | undefined
): AskedRecord<RecordPermissionsEntity> | undefined =>
  authorization.kind === 'record' && recordId != null ? { entity: authorization.entity, recordId } : undefined

/**
 * Whether one rule admits the current caller, where withheld answers read as no. A surface that must
 * say something distinct while a check is in flight reads `useAccessSources` and calls `resolveAccess`
 * for the four-state answer instead.
 *
 * The overloads pair a record rule with the id it is asked about, so a rule naming a verb cannot reach
 * the evaluator without the record it names. The id may be absent — a page whose record is still
 * loading, or a tab whose request has no supplier — and answers no rather than throwing.
 */
export function useIsPermitted(rule: NonRecordAccessRule): boolean
export function useIsPermitted(rule: RecordAccessRule, recordId: string | undefined): boolean
export function useIsPermitted(rule: NonRecordAccessRule | RecordAccessRule, recordId?: string): boolean {
  const sources = useAccessSources(askedFor(rule.authorization, recordId))

  if (rule.authorization.kind === 'record' && recordId == null) return false

  return resolveAccess(rule, sources) === 'allowed'
}
